import React, { Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, Link } from "react-router-dom";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Container, Navbar, Nav, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { VscBook } from "react-icons/vsc";
import Navigation from 'context/nav';
import ActingPill from 'components/controls/actingPill';
import Footer from 'components/controls/Footer/index';
import { HeaderSlot } from 'components/controls/HeaderSlot/index';
import SessionEndedModal from 'components/controls/sessionEndedModal';
import ThemeMenu from 'components/controls/ThemeMenu/index';
import Main from '../Main/index';
import Profile from '../Profile/index';
import OrgCreate from '../Org/create';
import Org from '../Org/index';
import Blob from '../Blob/index';
import BlobCreate from '../Blob/create';
import BlobRevisions from '../Blob/revisions';
import UserMenu from '../Auth/user';
import NotFound from '../Main/notFound';
import Api from 'context/api';
import { Terms, Cookies, Privacy, Beta } from "pages/Main/legal";

export default class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      screenMode: "normal",
      authenticated: false,
      auth: null,
      sessionNotice: null,
      api: null
    };

    this.state.api = new Api(
      this.state,
      (state) => {
        const promise = new Promise((resolve, reject) => {
          this.setState(state, () => {
            resolve();
          });
        });
        return promise;
      },
      () => { return this.state });
  }

  componentDidMount() {
    this.state.api.startExpiryTimer();
  }

  componentWillUnmount() {
    this.state.api.stopExpiryTimer();
  }

  handleScreenModeChange = (value, event) => {
    this.setState({
      screenMode: value
    });
  }

  render() {
    const PrivateRoutes = (props) => {
      return ( props.app.authenticated ? <Outlet/> : <Navigate to='/'/> )
    }

    // A PARAM-ONLY NAVIGATION REUSES THIS ELEMENT. `/p/blobhub` -> `/p/me` matches the SAME <Route>,
    // so react-router hands React the same element at the same position with no key: React
    // reconciles rather than remounts, componentDidMount never re-runs, and a page whose fetch lives
    // there keeps rendering the PREVIOUS resource under the new URL — with no loading state and no
    // error, because the `!this.state.user` early return never fires either.
    //
    // Keying on the params that NAME THE RESOURCE makes an identity change a remount. This is done
    // here rather than as a componentDidUpdate per page because the fetch is not in one place: three
    // page classes have children that each fetch in their own componentDidMount, so an honest
    // componentDidUpdate would have to null every state field to unmount them, in every class,
    // forever — and it would silently drop the per-view effects that fire once per mount
    // (ReactGA.pageview, Title.set, Blob's Preferences.subscribe and keydown handler).
    //
    // `section`/`sectionId`/`subSectionId` are deliberately NOT in any identity. Blob's four section
    // routes are siblings at one level, so a key covering them would remount the whole workflow shell
    // on every tab switch. (Profile's and Org's section routes sit one level deeper, inside
    // <PrivateRoutes>, so the matched tree already differs in depth and they remount on a tab switch
    // today — the key changes nothing there. Blob is the only reason this exclusion exists.)
    //
    // `identity` MUST be destructured out, or it leaks through ...rest onto the page as a stray prop.
    const RouteWrapper = ({component: Component, identity, ...rest}) => {
      const params = useParams();
      const location = useLocation();
      const navigate = useNavigate();
      const key = (identity || []).map((name) => params[name]).join("/");
      return <Component
        key={key}
        app={this.state}
        {
          ...{
            ...rest,
            match: {
              params: params,
              path: location.pathname
            },
            history: Navigation.historyShim(navigate)
          }
        }
      />
    }

    return (
      <Router>
        <Navbar bg="dark" variant="dark" expand="lg" className="shadow-sm">
          <Container>
            <Navbar.Brand as={Link} to="/">BlobHub</Navbar.Brand>
            &nbsp;&nbsp;
            <Nav className="mr-auto navbar-featured">
              <Nav.Link as={Link} to="/p/blobhub">Featured</Nav.Link>
            </Nav>
            <HeaderSlot id="blob-extras" className="blob-header-slot"/>
            <Nav className="navbar-end-nav">
              <Nav.Link href="//docs.blobhub.io">
                <VscBook/>
                <span className="navbar-link-label">&nbsp;&nbsp;Docs</span>
              </Nav.Link>
              {/* No `&nbsp;` spacer between these three. Every navbar item carries its own 0.5rem
                  horizontal padding from `.navbar .nav-link` (pages/Blob/index.css:316), so adjacent
                  items are already separated by 16px of padding and the entities were a second,
                  competing spacing system on top of it. With the spacer, this control sat 12.6px from
                  Docs and 0.0px from the avatar — measured — because it was inserted after the entity
                  rather than between two of them. Deleting it makes one rule own the rhythm.
                  UserMenu stays last because it renders either a dropdown or a `Sign in` button
                  depending on auth state, and a control whose width changes with authentication is
                  the wrong thing to anchor the right edge against. */}
              <ThemeMenu/>
              <UserMenu
                app={this.state}
              />
            </Nav>
          </Container>
        </Navbar>

        <Routes>
          <Route path="/" element={<Main/>} />

          <Route path="/terms" element={<Terms/>} />
          <Route path="/privacy" element={<Privacy/>} />
          <Route path="/cookies" element={<Cookies/>} />
          <Route path="/beta" element={<Beta/>} />

          <Route element={<PrivateRoutes app={this.state}/>}>
            <Route path="/p/:userId/org/create" element={<RouteWrapper component={OrgCreate} identity={["userId"]}/>} />

            <Route path="/p/:userId/:section" element={<RouteWrapper component={Profile} identity={["userId"]}/>} />
            <Route path="/p/:userId/:section/:sectionId"
                   element={<RouteWrapper component={Profile} identity={["userId"]}/>} />

            <Route path="/:orgId/settings" element={<RouteWrapper component={Org} identity={["orgId"]}/>} />
            <Route path="/:orgId/settings/:sectionId" element={<RouteWrapper component={Org} identity={["orgId"]}/>} />

            <Route path="/:orgId/blob/create" element={<RouteWrapper component={BlobCreate} identity={["orgId"]}/>} />
          </Route>

          <Route path="/p/:userId" element={<RouteWrapper component={Profile} identity={["userId"]}/>} />

          <Route path="/:orgId" element={<RouteWrapper component={Org} identity={["orgId"]}/>} />

          <Route path="/:orgId/:blobId" element={<RouteWrapper component={Blob} identity={["orgId","blobId"]}/>} />
          <Route path="/:orgId/:blobId/:section"
                 element={<RouteWrapper component={Blob} identity={["orgId","blobId"]}/>} />
          <Route path="/:orgId/:blobId/:section/:sectionId"
                 element={<RouteWrapper component={Blob} identity={["orgId","blobId"]}/>} />
          <Route path="/:orgId/:blobId/:section/:sectionId/:subSectionId"
                 element={<RouteWrapper component={Blob} identity={["orgId","blobId"]}/>} />

          <Route path="*" element={<NotFound/>} />
        </Routes>

        <SessionEndedModal
          notice={this.state.sessionNotice}
          onAcknowledge={() => Navigation.reload()}
        />

        {this.state.api.isActing() &&
          <ActingPill
            session={this.state.auth}
            api={this.state.api}
            onExit={() => this.state.api.stopActing(false)}
          />
        }

        <Footer/>
      </Router>
    );
  }
}
